import React, { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { appInsights } from '@/telemetry/client';
import { useAppSelector, type AppRootState } from '@/configureStore';
import { useAddSingleItemToCartMutation } from '@/stores/cartApi';
import { useLazyGetUserPriceByCodeQuery } from '@/stores/productsApi';
import { selectCurrentUser } from '@/stores/currentUserApi';
import { PriceDisplaySimple } from '@/components/PriceDisplay';
import { AddToCartModal } from '@/components/AddToCartModal';
import { ProductAttributeSelector } from './Components/ProductAttributeSelector';
import { Labels } from './Components/Labels';
import { Media } from './Components/Media';
import styles from './ProductTile.module.scss';
import type * as Types from '@/stores/productsApiTypes';

function findMatchingVariant(product: Types.Product, facetsDictionary?: Types.FacetsDictionary): Types.Variant {
  const defaultVariant = product.variants.find((variant) => variant.code === product.defaultVariantCode) ?? product.variants[0];

  if (!facetsDictionary) {
    return defaultVariant;
  }

  const variant = product.variants.find((variant: Types.Variant) => {
    const entries = Object.entries(facetsDictionary);
    return entries.every(([key, values]) => {
      //@ts-expect-error: typings incorrect
      const res = values.indexOf(variant.variantAttributes[key]) !== -1;
      return res;
    });
  });

  return variant ?? defaultVariant;
}

interface ProductTileProps {
  product: Types.Product;
  facetsDictionary?: Types.FacetsDictionary;
  disableLink?: boolean;
  disableModal?: boolean;
  addToCartCallback?: (event: React.MouseEvent<HTMLButtonElement>, extraArgs: any) => void;
}

export const ProductTile = (props: ProductTileProps) => {
  const { t } = useTranslation('common');
  const { product } = props;
  const { variants } = product;
  const { labels = [] } = product;
  const currentVariant = findMatchingVariant(product, props?.facetsDictionary);
  const currentUser = useAppSelector((state: AppRootState) => selectCurrentUser(state));
  const [fetchUserPrice, { isLoading: isUserPriceLoading, isFetching: isUserPriceFetching, isUninitialized: isUserPriceUninitialized }] =
    useLazyGetUserPriceByCodeQuery();
  const [addToCart] = useAddSingleItemToCartMutation();
  const [showAddToCartModal, setShowAddToCartModal] = useState<boolean>(false);
  const [variant, setVariant] = useState(currentVariant);
  const [unitOfMeasure, setUnitOfMeasure] = useState(variant.unitsOfMeasure[0]);
  const [quantity, setQuantity] = useState<number>(1);
  const [attributes, setAttributes] = useState({ ...variant.variantAttributes });
  const [selectableVariantAttributes, setSelectableVariantAttributes] = useState(product.selectableVariantAttributes);
  const [productPrices, setProductPrices] = useState({
    exTax: {
      numeric: quantity * unitOfMeasure?.prices.current.exTax.numeric,
      formatted: ''
    },
    incTax: {
      numeric: quantity * unitOfMeasure?.prices.current.incTax.numeric,
      formatted: ''
    }
  });

  useEffect(() => {
    if (props?.facetsDictionary) {
      const nextVariant = findMatchingVariant(product, props?.facetsDictionary);
      setVariant(nextVariant);
      setAttributes(nextVariant.variantAttributes);
    }
  }, [product, props.facetsDictionary]);

  useEffect(() => {
    // TODO: Implement user prices in the backend. Making a call for each and every product is inefficient
    if (!currentUser) {
      return;
    }
    const getUserPrices = async () => {
      const userPrice = await fetchUserPrice({
        unitOfMeasureCode: unitOfMeasure.code,
        variantCode: variant.code
      }).unwrap();
      setProductPrices(userPrice.current);
    };
    getUserPrices();
  }, [currentUser, unitOfMeasure, variant, fetchUserPrice]);

  useEffect(() => {
    const nextVariant = variants.find((variant) => {
      return Object.entries(attributes).every(([key, value]) => {
        return variant.variantAttributes[key] === value;
      });
    });

    if (nextVariant) {
      const nextUnitOfMeasure = nextVariant.unitsOfMeasure.find((uom) => uom.code === unitOfMeasure.code) ?? nextVariant.unitsOfMeasure[0];
      setUnitOfMeasure(nextUnitOfMeasure);
      setVariant(nextVariant);
    }

    // Find first available combination of attributes
    if (!nextVariant) {
      const firstValidAttribute = selectableVariantAttributes.find((attr) => attr.type !== '1400');
      const firstValidAttributeValue = firstValidAttribute?.values.find((attr) => {
        return attr.disabled === false;
      });

      if (firstValidAttribute && firstValidAttributeValue) {
        setAttributes({
          ...attributes,
          [firstValidAttribute.type]: firstValidAttributeValue.code
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes]);

  type IHandleAttributeChange = {
    type: string;
    attributeValue: Types.Attribute;
  };
  const handleAttributeChange = ({ type, attributeValue }: IHandleAttributeChange) => {
    const updatedAttributes = product.selectableVariantAttributes.map((attribute) => {
      if (attribute.type === type) {
        // return unmodified attribute
        return attribute;
      }
      const validValues = new Set(
        variants
          .filter((variant) => variant.variantAttributes[type] === attributeValue.code)
          .map((variant) => variant.variantAttributes[attribute.type])
          .flat() || []
      );

      const updatedValues = [
        ...attribute.values.map((value) => ({
          ...value,
          disabled: !validValues.has(value.code)
        }))
      ];
      return {
        code: attribute.code,
        type: attribute.type,
        name: attribute.name,
        values: updatedValues
      };
    });

    setAttributes({
      ...attributes,
      [type]: attributeValue.code
    });

    setSelectableVariantAttributes(updatedAttributes);
  };

  const handleUnitOfMeasureChange = ({ code }: { code: string }) => {
    const nextUnitOfMeasure = variant.unitsOfMeasure.find((uom) => uom.code === code);

    if (!nextUnitOfMeasure) {
      return;
    }

    if (!currentUser) {
      const { exTax, incTax } = nextUnitOfMeasure.prices.current;

      setProductPrices({
        exTax: {
          numeric: quantity * exTax.numeric,
          formatted: ''
        },
        incTax: {
          numeric: quantity * incTax.numeric,
          formatted: ''
        }
      });
    }

    setUnitOfMeasure(nextUnitOfMeasure);
  };

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10) || 0;
    setQuantity(value);
  };

  const handleAddToCart = async (event: React.MouseEvent<HTMLButtonElement>) => {
    try {
      if (!props.disableModal) {
        setShowAddToCartModal(true);
      }
      await addToCart({
        variantCode: variant.code,
        unitOfMeasureCode: unitOfMeasure.code,
        quantity: quantity
      });

      typeof props.addToCartCallback === 'function' && props.addToCartCallback(event, { variantCode: variant.code });
    } catch (error) {
      appInsights.trackException({ exception: error as Error });
    }
  };

  const shouldShowPrices = !isUserPriceLoading && !isUserPriceFetching && !isUserPriceUninitialized;
  return (
    <>
      <AddToCartModal showModal={showAddToCartModal} onHideModal={() => setShowAddToCartModal(false)} productCount={quantity} />

      <div className={`${styles.ProductTile}`}>
        <div className={`${styles.componentContent} px-3`}>
          {labels.length > 0 && <Labels labels={labels} />}

          {props.disableLink ? (
            <Media media={variant.media} />
          ) : (
            <a href={variant?.url}>
              <Media media={variant.media} />
            </a>
          )}

          <div className={`${styles.Meta}`}>
            <h3 className={`${styles.h3} h3 mb-0`}>
              <a href={variant.url ?? product.url}>{variant.title}</a>
            </h3>
            <p className="text-sans-serif text-muted mb-3">
              <a href={variant.url ?? product.url}>{t('common:prefix.product-code', { code: variant?.code })}</a>
            </p>
          </div>

          <div className={`${styles.Prices}`}>
            <PriceDisplaySimple
              price={productPrices}
              quantity={quantity}
              className={`${!shouldShowPrices ? styles.userPriceLoading : ''}`}
            />
          </div>

          {selectableVariantAttributes.length > 0 && (
            <div className={styles.VariantOptionsContainer}>
              <ProductAttributeSelector
                onAttributeChange={handleAttributeChange}
                // @ts-expect-error: wtf TS? `| never[]`
                attributes={attributes}
                selectableVariantAttributes={selectableVariantAttributes}
              />
            </div>
          )}

          <div className={`${styles.AddToCart} row pb-3 mx-0`}>
            <div className="col pl-0 pr-1">
              {variant.unitsOfMeasure.length > 1 ? (
                <select
                  className={`${styles.AddToCartAmountUOM} form-control custom-select`}
                  value={unitOfMeasure.code}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleUnitOfMeasureChange({ code: e.target.value })}
                >
                  {variant.unitsOfMeasure.map((uom: any) => (
                    <option value={uom.code} key={uom.code}>
                      {uom.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input className={`${styles.AddToCartAmount} form-control custom-control`} value={unitOfMeasure.name} readOnly />
              )}
            </div>

            <div className="col pr-0 pl-1">
              <div className="input-group">
                <input
                  onChange={handleQuantityChange}
                  value={quantity}
                  type="number"
                  min="1"
                  className={`form-control ${styles.AddToCartAmount} pr-0`}
                  name="amount"
                  autoComplete="off"
                  placeholder={t('shopping-cart:quantity.field')}
                  aria-label="aantal"
                />
                <div className="input-group-append">
                  <button className={`${styles.AddToCartButton} btn btn-cart`} type="button" onClick={handleAddToCart}>
                    <i className="fa cl-cart fa-lg"></i>
                    <span className="sr-only">{t('common:button.add-to-car')}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
